import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { apiService } from '@/services/api'
import DonateModal from '@/components/DonateModal'
import type { ReserveSummary } from '@/types'

function DonateMenu() {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<ReserveSummary | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['reserves'],
    queryFn: () => apiService.getReserves(),
  })
  const reserves = data?.data ?? []

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <>
      <div className="relative" ref={containerRef}>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center gap-2 bg-bitcoin hover:bg-bitcoin-dark text-white font-semibold py-2 px-5 rounded-lg transition-colors"
        >
          Donate
          <ChevronDown size={16} />
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden"
          >
            {isLoading && <div className="px-4 py-3 text-gray-400 text-sm">Loading…</div>}
            {!isLoading && reserves.length === 0 && (
              <div className="px-4 py-3 text-gray-400 text-sm">No reserves yet</div>
            )}
            {reserves.map((reserve) => (
              <button
                key={reserve.id}
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  setSelected(reserve)
                }}
                className="block w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors"
              >
                <div className="text-white font-medium">{reserve.name}</div>
                <div className="text-gray-400 text-sm">
                  {reserve.city}, {reserve.state}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected &&
        createPortal(
          <DonateModal
            reserveSlug={selected.slug}
            reserveName={selected.name}
            onClose={() => setSelected(null)}
          />,
          document.body
        )}
    </>
  )
}

export default function Header() {
  const { pathname } = useLocation()

  return (
    <header className="bg-gray-900 shadow-lg sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-bitcoin hover:text-bitcoin-light transition-colors">
            C₿R
          </Link>
          {pathname === '/' && <DonateMenu />}
        </div>
      </nav>
    </header>
  )
}
